import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { InvoiceDataService } from '../invoice-data.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { forkJoin, Observable, pipe, switchMap } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { StandaloneInvoiceDraft } from './types/StandaloneInvoiceDraft';
import { ClientDataService } from '@lib/client';
import { CustomerDepartmentDto } from '@lib/department/ui';
import { IimFilterableRecord } from '@iim-angular/components';
import { ICompanyAddressWithContact } from '@lib/address';
import { ICreateInvoiceCommandDto } from './types/ICreateStandaloneInvoiceCommand';
import { IInvoiceCreateResponse } from './types/IInvoiceCreateResponse';
import { Router } from '@angular/router';
import { View } from '@lib/core';
import { InvoiceSettingsDto } from '../types/InvoiceSettingsDto';

interface CreateStandaloneInvoiceState {
    receiptIds: number[];
    invoice: StandaloneInvoiceDraft | null;
    isLoading: boolean;
    currencies: IimFilterableRecord<string>[];
    invoiceSettings: InvoiceSettingsDto | null;
    fixedSupplier: boolean;
    fixedDepartment: boolean;
    invoiceId: string | null;
    departments: CustomerDepartmentDto[];
    taskId: string | null;
}

const initialState: CreateStandaloneInvoiceState = {
    receiptIds: [],
    invoice: null,
    isLoading: false,
    currencies: [],
    invoiceSettings: null,
    fixedSupplier: false,
    fixedDepartment: false,
    invoiceId: null,
    departments: [],
    taskId: null,
};

export const CreateOrUpdateStandaloneInvoiceStore = signalStore(
    withState(initialState),
    withMethods(
        (
            store,
            dataService = inject(InvoiceDataService),
            clientDataService = inject(ClientDataService),
            router = inject(Router),
        ) => ({
            setTaskId: (taskId: string | null) => {
                patchState(store, { taskId });
            },
            /**
             * Updates the supplier of the invoice
             * @param supplier The supplier to set
             */
            updateSupplier: (supplier: { id: string; name: string; supplierNumber?: string | null }) => {
                const currentInvoice = store.invoice();
                if (currentInvoice) {
                    patchState(store, {
                        invoice: currentInvoice.withSupplier(supplier),
                    });
                }
            },

            clearSupplier: () => {
                const currentInvoice = store.invoice();
                if (currentInvoice) {
                    patchState(store, {
                        invoice: currentInvoice.withoutSupplier(),
                    });
                }
            },

            updateDepartment: (department: CustomerDepartmentDto) => {
                const currentInvoice = store.invoice();
                if (currentInvoice) {
                    patchState(store, {
                        invoice: currentInvoice.withDepartment(department),
                    });
                }
            },

            /**
             * Updates the company address of the invoice
             * @param companyAddress The company address to set
             */
            updateAddress: (companyAddress: ICompanyAddressWithContact) => {
                const currentInvoice = store.invoice();
                if (currentInvoice) {
                    patchState(store, {
                        invoice: currentInvoice.withAddress(companyAddress),
                    });
                }
            },

            clearAddress: () => {
                const currentInvoice = store.invoice();
                if (currentInvoice) {
                    patchState(store, {
                        invoice: currentInvoice.withoutAddress(),
                    });
                }
            },

            updateCompanyCode: (companyCodeId: string) => {
                const currentInvoice = store.invoice();
                if (currentInvoice) {
                    patchState(store, {
                        invoice: currentInvoice.withCompanyCode(companyCodeId),
                    });
                }
            },

            /**
             * Loads initial data for a standalone invoice
             * Executes a series of asynchronous operations to fetch the data from the `dataService`.
             * The `isLoading` state is updated during the process to reflect the loading status.
             * On successful data retrieval, the state is updated with the received invoice data and currencies.
             * If an error occurs during data retrieval, the `isLoading` state is reset.
             */
            loadInitialData: rxMethod<number[]>(
                pipe(
                    tap((receiptIds: number[]) =>
                        patchState(store, { isLoading: true, receiptIds: receiptIds?.length > 0 ? receiptIds : [] }),
                    ),
                    switchMap((receiptIds: number[]) =>
                        forkJoin({
                            invoice: dataService.getStandaloneInitialData(receiptIds),
                            currencies: clientDataService
                                .getActiveCurrencies()
                                .pipe(
                                    map((currencies) =>
                                        currencies.map((currency) => new IimFilterableRecord<string>(currency)),
                                    ),
                                ),
                            invoiceSettings: dataService.getInvoiceSettings(),
                            departments: dataService.getDepartments(),
                        }).pipe(
                            tap({
                                next: ({ invoice, currencies, invoiceSettings, departments }) => {
                                    const standaloneInvoice = new StandaloneInvoiceDraft(invoice);
                                    patchState(store, {
                                        invoice: standaloneInvoice,
                                        currencies,
                                        invoiceSettings,
                                        departments,
                                        isLoading: false,
                                        fixedSupplier: standaloneInvoice.hasReceiptRelation,
                                        fixedDepartment: standaloneInvoice.hasReceiptRelation,
                                    });
                                },
                                error: () => {
                                    patchState(store, { isLoading: false });
                                },
                            }),
                        ),
                    ),
                ),
            ),

            loadInitialDataWithInvoiceId: rxMethod<string>(
                pipe(
                    tap((invoiceId: string) => patchState(store, { isLoading: true, invoiceId })),
                    switchMap((invoiceId: string) =>
                        forkJoin({
                            invoice: dataService.getStandaloneInitialDataWithInvoiceId(invoiceId),
                            currencies: clientDataService
                                .getActiveCurrencies()
                                .pipe(
                                    map((currencies) =>
                                        currencies.map((currency) => new IimFilterableRecord<string>(currency)),
                                    ),
                                ),
                            departments: dataService.getDepartments(),
                            invoiceSettings: dataService.getInvoiceSettings(),
                        }).pipe(
                            tap({
                                next: ({ invoice, currencies, invoiceSettings, departments }) => {
                                    const standaloneInvoice = new StandaloneInvoiceDraft(invoice);
                                    patchState(store, {
                                        invoice: standaloneInvoice,
                                        currencies,
                                        invoiceSettings,
                                        departments,
                                        isLoading: false,
                                    });
                                },
                                error: () => {
                                    patchState(store, { isLoading: false });
                                },
                            }),
                        ),
                    ),
                ),
            ),

            onSaveDraft: (
                command: ICreateInvoiceCommandDto,
                invoiceId: string,
                files?: FormData,
                scannedImages?: FormData,
                taskId?: string,
            ): Observable<void> => {
                patchState(store, { isLoading: true });
                return dataService.onSaveDraft(command, invoiceId, files, scannedImages, taskId).pipe(
                    tap({
                        next: () => {
                            patchState(store, {
                                isLoading: false,
                            });
                        },
                        error: () => patchState(store, { isLoading: false }),
                    }),
                    tap(() => {
                        if (!store.taskId()) {
                            void router.navigate([View.CLIENT, 'my-admin', 'invoice', invoiceId, 'detail']);
                        }
                    }),
                );
            },

            onSaveForApproval: (
                command: ICreateInvoiceCommandDto,
                invoiceId: string,
                files?: FormData,
                scannedImages?: FormData,
            ): Observable<void> => {
                patchState(store, { isLoading: true });
                return dataService.onSaveForApproval(command, invoiceId, files, scannedImages).pipe(
                    tap({
                        next: () => {
                            patchState(store, {
                                isLoading: false,
                            });
                        },
                        error: () => patchState(store, { isLoading: false }),
                    }),
                    tap(() => {
                        if (!store.taskId()) {
                            router.navigate([View.CLIENT, 'my-admin', 'invoice', invoiceId, 'detail']);
                        }
                    }),
                );
            },

            /**
             * Saves the invoice by calling the data service
             * @param command The command DTO to save
             * @param files Optional FormData containing files to attach
             * @param scannedImages Optional FormData containing scanned images to attach
             * @returns An Observable of the saved invoice ID
             */
            saveInvoice: (
                command: ICreateInvoiceCommandDto,
                files?: FormData,
                scannedImages?: FormData,
            ): Observable<IInvoiceCreateResponse> => {
                patchState(store, { isLoading: true });
                return dataService.createStandaloneInvoice(command, files, scannedImages).pipe(
                    tap({
                        next: () => patchState(store, { isLoading: false }),
                        error: () => patchState(store, { isLoading: false }),
                    }),
                    tap((response: IInvoiceCreateResponse) => {
                        const invoiceId = response.invoiceId;
                        const createdFromReceipts = (store.receiptIds() ?? []).length > 0;

                        if (createdFromReceipts) {
                            router.navigate(
                                [
                                    View.CLIENT,
                                    'my-admin',
                                    'invoice',
                                    'create',
                                    'standalone',
                                    'success',
                                    invoiceId,
                                ],
                                {
                                    state: {
                                        invoiceNumber: command.invoiceNumber,
                                        detailAllowed: response.detailAllowed,
                                        possibleActions: response.possibleActions,
                                    },
                                },
                            );
                        } else {
                            router.navigate([View.CLIENT, 'my-admin', 'invoice', invoiceId, 'detail']);
                        }
                    }),
                );
            },
        }),
    ),
);
